<!-- This component is used to display PlanHolder's documents and steps in the left side bar. -->
<template>
	<section
		v-if="steps"
		class="transition-width bg-background-50 relative z-20 flex h-full shrink-0 flex-col justify-between gap-1 pt-6 duration-300"
	>
		<button
			v-if="!expanded"
			type="button"
			class="bg-background-50! absolute top-8 left-full z-20 py-2"
			@click="emit('expand')"
		>
			<ChevronRightIcon class="text-primary-700 h-6 w-6 text-xs" />
		</button>
		<div
			class="flex h-screen flex-col gap-2 overflow-auto"
			:class="expanded ? 'items-between' : 'items-center'"
		>
			<template
				v-for="(planHolder, index) in sessionStore.activePlans"
				:key="planHolder.id"
			>
				<StepAccordion
					v-if="sessionDocumentsStore.steps[planHolder.id]"
					:plan="planHolder"
					:index="index"
					:defaultOpen="
						sessionDocumentsStore.currentPlanHolderID === planHolder.id
					"
					:expanded="expanded"
					:disabled="planHolder.packetStatus !== PlanPacketStatus.Incomplete"
				>
					<button
						v-for="step in steps[planHolder.id]"
						:key="step.label"
						type="button"
						:aria-label="step.label"
						:data-step-type="step.type"
						class="hover:bg-secondary-50! mx-5 flex items-center gap-3 border-b border-slate-200! py-3 text-left text-sm font-bold uppercase last:border-b-0 disabled:cursor-not-allowed disabled:opacity-50"
						:class="{
							'text-accent-500!': step.active,
							'text-secondary-900!': !step.active,
						}"
						:disabled="isStepDisabled(step, planHolder.id)"
						@click="setCurrentStep(step, planHolder.id)"
					>
						<span
							class="border-accent-400! text-accent-400! flex size-6 min-w-6 items-center justify-center rounded-full border text-xl"
							:class="{ 'bg-accent-400': step.completed }"
							:title="t(step.title)"
						>
							<CheckIcon
								class="size-6 h-4 w-4 text-white"
								v-show="step.completed"
							/>
						</span>
						<span
							v-if="expanded"
							class="animate-fade-in-delay text-sm opacity-0"
						>
							{{ t(step.title) }}
						</span>
					</button>

					<SupplementalAccordion
						v-if="
							sessionDocumentsStore.supplementalSteps[planHolder.id].length > 0
						"
						:plan="planHolder"
						:index="index"
						:defaultOpen="false"
						:expanded="expanded"
						:disabled="
							!sessionDocumentsStore.isConfirmationCompleted(planHolder.id)
						"
					>
						<button
							v-for="step in supplementalSteps[planHolder.id]"
							:key="step.label"
							type="button"
							:aria-label="step.label"
							:data-step-type="step.type"
							class="mx-5 flex items-center gap-3 border-b-2 border-slate-200! py-2 text-left text-sm font-bold uppercase hover:bg-[#e6e9f0]! disabled:cursor-not-allowed disabled:opacity-50"
							:class="{
								'text-accent-500!': step.active,
								'text-secondary-900!': !step.active,
							}"
							:disabled="isStepDisabled(step, planHolder.id)"
							@click="setCurrentStep(step, planHolder.id)"
						>
							<span
								class="border-accent-400 text-accent-400 flex h-8 w-8 min-w-8 items-center justify-center rounded-full border-2 text-xl"
								:class="{ 'bg-accent-400': step.completed }"
								:title="t(step.title)"
							>
								<CheckIcon
									class="size-6 h-4 w-4 text-white"
									v-show="step.completed"
								/>
							</span>
							<span
								v-if="expanded"
								class="animate-fade-in-delay text-sm opacity-0"
							>
								{{ t(step.title) }}
							</span>
						</button>
					</SupplementalAccordion>
				</StepAccordion>
			</template>
		</div>

		<div class="flex flex-col">
			<button
				type="button"
				data-test="finish-and-review-button"
				class="flex w-full items-center justify-between border-t-2 border-slate-200! px-4 py-4 text-sm font-bold uppercase hover:bg-[#e6e9f0]!"
				:class="{
					'text-accent-500!': isOnFinishAndReview,
					'text-secondary-900!': !isOnFinishAndReview,
				}"
				:disabled="pageIsLoading"
				@click="goToFinishAndReview"
			>
				<span v-if="expanded">{{ t('document.finishAndReview') }}</span>
				<ArrowLongRightIcon
					class="h-5 w-5"
					:class="isOnFinishAndReview ? 'text-accent-500' : 'text-slate-500'"
				/>
			</button>
			<div v-if="!expanded">
				<button
					type="button"
					class="text-secondary-900! flex w-full items-center justify-center pb-4 text-xl font-bold"
					@click="expandAndOpenMore"
				>
					...
				</button>
			</div>
			<Disclosure
				v-else
				:defaultOpen="moreButtonOpen"
				v-slot="{ open }"
			>
				<DisclosureButton
					class="flex w-full items-center justify-between border-t-2 border-slate-200! px-4 py-4 text-sm font-bold uppercase"
				>
					<span :class="open ? 'text-primary-600' : 'text-secondary-900'">
						{{ t('document.exitTo') }}
					</span>
					<ChevronRightIcon
						v-if="expanded"
						:class="{ 'rotate-90 transform': open }"
						class="h-5 w-5 text-slate-500 transition-transform duration-200"
					/>
				</DisclosureButton>
				<DisclosurePanel>
					<div class="animate-fade-in-delay pb-3 opacity-0">
						<button
							type="button"
							:disabled="pageIsLoading"
							class="text-secondary-900! flex w-full py-1.5 pl-4 text-sm font-bold uppercase hover:bg-[#e6e9f0]! disabled:cursor-not-allowed disabled:opacity-50"
							@click="
								router.push(
									`/session/${route.params.sessionId}/present?slide=payment-options-slide`
								)
							"
						>
							{{ t('document.paymentBreakdown') }}
						</button>
						<button
							type="button"
							class="flex w-full items-center py-1.5 pl-4 text-sm font-bold uppercase hover:bg-[#e6e9f0]! disabled:cursor-not-allowed disabled:opacity-50"
							:disabled="pageIsLoading"
							@click="
								router.push(
									`/session/${route.params.sessionId}/present?slide=review-slide`
								)
							"
						>
							{{ t('document.shoppingCart') }}
						</button>
						<button
							type="button"
							@click="exitToHomePage"
							:disabled="pageIsLoading"
							class="text-secondary-900! flex w-full py-1.5 pl-4 text-sm font-bold uppercase hover:bg-[#e6e9f0]!"
						>
							{{ t('document.dashboardHome') }}
						</button>
					</div>
				</DisclosurePanel>
			</Disclosure>
		</div>
	</section>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import StepAccordion from '@/components/documents/LeftSideBar/StepAccordion.vue'
import SupplementalAccordion from '@/components/documents/LeftSideBar/SupplementalAccordion.vue'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { usePresentationLocale } from '@/i18n'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import type { PageStatusType, Step } from '@/stores/sessionDocuments'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { StoreDocument } from '@/types'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import {
	CheckIcon,
	ChevronRightIcon,
	ArrowLongRightIcon,
} from '@heroicons/vue/24/solid'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const sessionDocumentsStore = useSessionDocumentsStore()
const sessionStore = useSessionStore()
const documentStore = useDocumentStore()
const route = useRoute()
const router = useRouter()
const { steps, supplementalSteps } = storeToRefs(sessionDocumentsStore)
const { t } = usePresentationLocale()
const moreButtonOpen = ref(false)

defineProps<{
	expanded: boolean
}>()

const emit = defineEmits<{
	(e: 'expand'): void
}>()

const cloudSyncService = useCloudSyncService()
const exitToHomePage = () => {
	cloudSyncService.flushPendingUpserts()
	router.push(`/`)
}
// This method will determine if a step is disabled based on the step's label, if the plan is encrypted, and the page's loading state.
const isStepDisabled = (step: Step, planHolderID: string): boolean => {
	if (pageIsLoading.value) {
		return true
	}

	if (step.label === 'confirmation') {
		return false
	}

	if (!sessionDocumentsStore.isConfirmationCompleted(planHolderID)) {
		return true
	}

	return sessionDocumentsStore.planHolderIsEncrypted(planHolderID)
}

// Sets the current step and planHolderID
const setCurrentStep = async (step: Step, planHolderID: string) => {
	await sessionDocumentsStore.setPageState('pending')
	sessionDocumentsStore.setStepActive(planHolderID, step)

	if (step.type === 'html') {
		sessionDocumentsStore.setCurrentPlanHolderID(planHolderID)
	}

	const pageState =
		step.type === 'html' ? (step.label as PageStatusType) : 'switch'
	const meta: { planHolderID: string; document?: StoreDocument } = {
		planHolderID,
	}

	if (step.type === 'document') {
		const planHolder = sessionStore.getPlanHolder(planHolderID) as Plan
		meta.document = documentStore.getPlanHolderDocumentByType(
			planHolder,
			step.label
		)
	}

	await sessionDocumentsStore.setPageState(pageState, meta)
}

// This property is used to ensure leftsidebar cannot be interacted with until document has finished loading.
const pageIsLoading = computed(() =>
	['merging', 'pending', 'switch'].includes(
		sessionDocumentsStore.pageState.status
	)
)

// Highlights Finish & Review like an active step while the complete step is showing
const isOnFinishAndReview = computed(
	() => sessionDocumentsStore.pageState.status === 'complete'
)

// This method will turn the planholder current step inactive.
// It also sets the page status to merging, triggering the FoxitPDFHandler to merge the documents.
// Once merging is complete, FoxitPDFHandler will set the page status to complete and show the complete step
const goToFinishAndReview = () => {
	sessionDocumentsStore.setCurrentStepInactive()
	sessionDocumentsStore.setPageState('merging')
}

// This method will expand the left sidebar and open the more button.
const expandAndOpenMore = () => {
	emit('expand')
	moreButtonOpen.value = true
}
</script>
